import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Calendar, Users, DollarSign, Sparkles, LogOut, Menu, X, TrendingUp, ListChecks, Mail, ChevronDown, Globe, User, Crown, Settings, Clock, FileText, ChevronRight, CheckCircle, Search, ExternalLink, MessageSquare, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import VALAssistant from '../components/VALAssistant';
import StartHereGuide from '../components/StartHereGuide';
import FeedbackSurvey from '../components/FeedbackSurvey';
import QuickFeedbackPrompt from '../components/QuickFeedbackPrompt';
import GuestListOnboarding from '../components/GuestListOnboarding';
import BudgetOnboarding from '../components/BudgetOnboarding';
import WebsiteOnboarding from '../components/WebsiteOnboarding';
import LocationOnboarding from '../components/LocationOnboarding';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [weddingPlan, setWeddingPlan] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [guests, setGuests] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [showVal, setShowVal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showWeddingDateForm, setShowWeddingDateForm] = useState(false);
  const [weddingDate, setWeddingDate] = useState('');
  const [guestWebsite, setGuestWebsite] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [guestsDropdownOpen, setGuestsDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [showFeedbackSurvey, setShowFeedbackSurvey] = useState(false);
  const [showQuickPrompt, setShowQuickPrompt] = useState(false);
  const [promptDayNumber, setPromptDayNumber] = useState(null);
  const [showGuestOnboarding, setShowGuestOnboarding] = useState(false);
  const [showBudgetOnboarding, setShowBudgetOnboarding] = useState(false);
  const [showWebsiteOnboarding, setShowWebsiteOnboarding] = useState(false);
  const [showLocationOnboarding, setShowLocationOnboarding] = useState(false);
  const [valMode, setValMode] = useState('general');
  const [onboardingSkips, setOnboardingSkips] = useState(() => {
    const saved = localStorage.getItem(`onboarding_skipped_${user.id}`);
    return saved ? JSON.parse(saved) : [];
  });

  // Save skips to localStorage
  useEffect(() => {
    localStorage.setItem(`onboarding_skipped_${user.id}`, JSON.stringify(onboardingSkips));
  }, [onboardingSkips, user.id]);

  const markStepAsSkipped = (stepId) => {
    if (!onboardingSkips.includes(stepId)) {
      setOnboardingSkips([...onboardingSkips, stepId]);
    }
  };

  const dropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setGuestsDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadDashboardData();
    checkFeedbackPrompt();
  }, [user.id]);

  const checkFeedbackPrompt = async () => {
    try {
      const response = await axios.get(`${API}/feedback/should-prompt/${user.id}`);
      if (response.data.should_prompt) {
        // Wait 5 seconds after page load to show prompt (less intrusive)
        setTimeout(() => {
          setPromptDayNumber(response.data.day_number);
          setShowQuickPrompt(true);
        }, 5000);
      }
    } catch (error) {
      console.error('Error checking feedback prompt:', error);
    }
  };

  const handleQuickPromptClose = (feedbackSubmitted) => {
    setShowQuickPrompt(false);
    if (feedbackSubmitted) {
      // If they submitted feedback, don't show again for 3 days
      // Backend handles this
    }
  };

  const loadDashboardData = async () => {
    setDataLoaded(false);
    try {
      const [planRes, tasksRes, guestsRes, budgetRes, websiteRes, chatRes] = await Promise.all([
        axios.get(`${API}/wedding-plans/${user.id}`),
        axios.get(`${API}/tasks/${user.id}`),
        axios.get(`${API}/guests/${user.id}`),
        axios.get(`${API}/budget-items/${user.id}`),
        axios.get(`${API}/guest-websites/user/${user.id}`),
        axios.get(`${API}/val/chat-history/${user.id}`)
      ]);
      
      setWeddingPlan(planRes.data);
      setTasks(tasksRes.data);
      setGuests(guestsRes.data);
      setBudgetItems(budgetRes.data);
      setGuestWebsite(websiteRes.data);
      setChatHistory(chatRes.data.has_chatted ? [1] : []); // Set array with item if user has chatted
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setDataLoaded(true);
    }
  };

  const handleSetWeddingDate = async (e) => {
    e.preventDefault();
    try {
      // Use PUT if plan exists, POST if not
      if (weddingPlan?.id) {
        await axios.put(`${API}/wedding-plans/${weddingPlan.id}`, 
          { wedding_date: weddingDate },
          { headers: { 'user-id': user.id } }
        );
      } else {
        await axios.post(`${API}/wedding-plans`, 
          { wedding_date: weddingDate },
          { headers: { 'user-id': user.id } }
        );
      }
      
      // Mark related tasks as complete
      await markTasksComplete(['Set your wedding date', 'Choose wedding date', 'Pick your wedding date']);
      
      toast.success('Wedding date set!');
      setShowWeddingDateForm(false);
      loadDashboardData();
    } catch (error) {
      console.error('Wedding date error:', error);
      toast.error('Failed to set wedding date');
    }
  };

  // Helper function to mark tasks as complete by title match
  // Marks ALL matching tasks (including duplicates and variations) to keep them in sync
  const markTasksComplete = async (taskTitles) => {
    try {
      for (const taskTitle of taskTitles) {
        const searchTerm = taskTitle.toLowerCase();
        
        // Find ALL tasks that match - be more aggressive with matching
        const matchingTasks = tasks.filter(t => {
          if (t.completed) return false;
          const title = t.title.toLowerCase();
          
          // Direct match
          if (title.includes(searchTerm)) return true;
          
          // Handle variations for common tasks
          if (searchTerm.includes('chat') && searchTerm.includes('val')) {
            return title.includes('chat') && title.includes('val');
          }
          if (searchTerm.includes('wedding date')) {
            return title.includes('wedding date');
          }
          if (searchTerm.includes('guest')) {
            return title.includes('guest') && (title.includes('add') || title.includes('create') || title.includes('list'));
          }
          if (searchTerm.includes('budget')) {
            return title.includes('budget') && (title.includes('set') || title.includes('your'));
          }
          
          return false;
        });
        
        // Mark all matching tasks as complete
        for (const task of matchingTasks) {
          await axios.put(`${API}/tasks/${task.id}/complete`);
        }
      }
    } catch (error) {
      console.error('Error marking tasks complete:', error);
    }
  };

  const calculateDaysUntil = () => {
    if (!weddingPlan?.wedding_date) return null;
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = weddingPlan.wedding_date.split('-').map(Number);
    const wedding = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to midnight for accurate day calculation
    const diff = Math.ceil((wedding - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const totalBudget = budgetItems.reduce((sum, item) => sum + item.estimated_cost, 0);
  const totalSpent = budgetItems.reduce((sum, item) => sum + (item.actual_cost || 0), 0);
  const completedTasks = tasks.filter(t => t.completed).length;

  // Get next uncompleted tasks for "What's Next" section
  // Filters out tasks that are already fulfilled via onboarding data
  const getNextTasks = () => {
    const incompleteTasks = tasks.filter(t => {
      // Skip already completed tasks
      if (t.completed) return false;
      
      const titleLower = t.title.toLowerCase();
      
      // Hide all "wedding date" tasks if date is set
      if (titleLower.includes('wedding date') && weddingPlan?.wedding_date) {
        return false;
      }
      
      // Hide all "chat with val" tasks if user has chatted
      if ((titleLower.includes('chat with val') || titleLower.includes('meet val')) && chatHistory.length > 0) {
        return false;
      }
      
      // Hide all guest-related tasks if user has guests
      if (titleLower.includes('guest') && guests.length > 0) {
        return false;
      }
      
      // Hide all budget-related tasks if budget is set
      if (titleLower.includes('budget') && weddingPlan?.total_budget > 0) {
        return false;
      }
      
      // Hide wedding location tasks if location is set
      if (titleLower.includes('location') && weddingPlan?.venue_location) {
        return false;
      }
      
      // Hide guest website tasks if website exists
      if (titleLower.includes('website') && guestWebsite) {
        return false;
      }
      
      return true;
    });
    
    // Remove semantic duplicates - keep only one task per concept
    // Priority: getting_started category first, then others
    const conceptGroups = {
      'wedding_location': [],
      'wedding_date': [],
      'chat_val': [],
      'guests': [],
      'budget': [],
      'other': []
    };
    
    incompleteTasks.forEach(t => {
      const titleLower = t.title.toLowerCase();
      if (titleLower.includes('location')) {
        conceptGroups.wedding_location.push(t);
      } else if (titleLower.includes('wedding date')) {
        conceptGroups.wedding_date.push(t);
      } else if (titleLower.includes('chat with val') || titleLower.includes('meet val')) {
        conceptGroups.chat_val.push(t);
      } else if (titleLower.includes('guest')) {
        conceptGroups.guests.push(t);
      } else if (titleLower.includes('budget')) {
        conceptGroups.budget.push(t);
      } else {
        conceptGroups.other.push(t);
      }
    });
    
    // For each concept, keep only ONE task (prefer getting_started category)
    const uniqueTasks = [];
    
    ['wedding_location', 'wedding_date', 'chat_val', 'guests', 'budget'].forEach(concept => {
      const group = conceptGroups[concept];
      if (group.length > 0) {
        // Prefer getting_started category, otherwise take the first one
        const preferred = group.find(t => t.category === 'getting_started') || group[0];
        uniqueTasks.push(preferred);
      }
    });
    
    // Add other tasks (non-onboarding tasks)
    uniqueTasks.push(...conceptGroups.other);
    
    // Return first 4
    return uniqueTasks.slice(0, 4);
  };

  const nextTasks = getNextTasks();

  // Handle task action (search or navigate)
  const handleTaskAction = (task) => {
    if (task.action_type === 'search' && task.action_search) {
      const location = weddingPlan?.venue_location || weddingPlan?.venue || '';
      const fullQuery = location ? `${task.action_search} near ${location}` : task.action_search;
      const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(fullQuery)}`;
      
      // Open in a new tab - no popup messages needed
      window.open(googleUrl, '_blank', 'noopener,noreferrer');
    } else if (task.action_type === 'internal' && task.action_link) {
      navigate(task.action_link);
    } else if (task.action_type === 'val') {
      // Open Grace assistant
      setShowVal(true);
    } else {
      // Default: go to checklist
      navigate('/checklist');
    }
  };

  // Mark task as complete
  const handleCompleteTask = async (taskId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/tasks/${taskId}/complete`);
      setTasks(tasks.map(t => t.id === taskId ? { ...t, completed: true } : t));
      toast.success('Task completed! 🎉');
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to update task');
    }
  };

  // Main nav items (without guest items)
  const navItems = [
    { label: 'Dashboard', path: '/dashboard', icon: Heart },
    { label: 'Details', path: '/details', icon: FileText },
    { label: 'Journey', path: '/checklist', icon: ListChecks },
    { label: 'Timeline', path: '/timeline', icon: Clock },
    { label: 'Budget', path: '/budget', icon: DollarSign },
    { label: 'Vendors', path: '/vendors', icon: Sparkles },
  ];

  // Guest dropdown items
  const guestMenuItems = [
    { label: 'Guest Website', path: '/guest-website', icon: Globe },
    { label: 'Guest List', path: '/guests', icon: Users },
    { label: 'Guest Manager', path: '/guest-manager', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      {/* Navigation */}
      <nav className="bg-white/70 backdrop-blur-xl border-b border-stone-200 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navItems.map(item => (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}-link`}
                onClick={() => navigate(item.path)}
                className="text-[#57534E] hover:text-[#881337] transition-colors duration-200 text-sm"
              >
                {item.label}
              </button>
            ))}
            
            {/* Guests Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                data-testid="nav-guests-dropdown"
                onClick={() => setGuestsDropdownOpen(!guestsDropdownOpen)}
                className="flex items-center gap-1 text-[#57534E] hover:text-[#881337] transition-colors duration-200 text-sm"
              >
                <Users size={16} />
                Guests
                <ChevronDown 
                  size={14} 
                  className={`transition-transform duration-200 ${guestsDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              
              {guestsDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-stone-200 py-2 z-50">
                  {guestMenuItems.map(item => (
                    <button
                      key={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}-link`}
                      onClick={() => {
                        navigate(item.path);
                        setGuestsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[#57534E] hover:bg-rose-50 hover:text-[#881337] transition-colors duration-200 text-sm"
                    >
                      <item.icon size={16} />
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="h-6 w-px bg-stone-200" />
            
            {/* User Dropdown */}
            <div className="relative" ref={userDropdownRef}>
              <button
                data-testid="user-dropdown"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-3 hover:bg-stone-100 rounded-full px-3 py-2 transition-colors"
              >
                <div className="text-right">
                  <p className="text-xs text-[#A8A29E]">Welcome back,</p>
                  <p className="text-sm font-medium text-[#1C1917]">{user.partner1_name}</p>
                </div>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#881337] to-[#9F1239] flex items-center justify-center text-white">
                  <User size={18} />
                </div>
                <ChevronDown 
                  size={14} 
                  className={`text-stone-400 transition-transform duration-200 ${userDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              
              {userDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-stone-200 py-2 z-50">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-stone-100">
                    <p className="font-medium text-[#1C1917]">{user.partner1_name} & {user.partner2_name}</p>
                    <p className="text-xs text-[#A8A29E]">{user.email}</p>
                  </div>
                  
                  {/* Subscription Status */}
                  <div className="px-4 py-3 border-b border-stone-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crown size={16} className={user.subscription_tier === 'premium' ? 'text-yellow-500' : 'text-stone-400'} />
                        <span className="text-sm text-[#57534E]">{user.subscription_tier === 'basic' ? 'Essentials' : 'Premium'} Plan</span>
                      </div>
                      {user.subscription_tier === 'basic' && (
                        <button
                          onClick={() => {
                            navigate('/upgrade');
                            setUserDropdownOpen(false);
                          }}
                          className="text-xs text-[#881337] hover:underline font-medium"
                        >
                          Upgrade
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Menu Items */}
                  <div className="py-1">
                    <button
                      data-testid="nav-account-btn"
                      onClick={() => {
                        navigate('/account');
                        setUserDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[#57534E] hover:bg-stone-50 transition-colors text-sm"
                    >
                      <Settings size={16} />
                      Account Settings
                    </button>
                    <button
                      data-testid="nav-logout-btn"
                      onClick={() => {
                        onLogout();
                        setUserDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[#57534E] hover:bg-stone-50 transition-colors text-sm"
                    >
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-1">
            {navItems.map(item => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-[#57534E] hover:bg-stone-100 rounded-lg"
              >
                {item.label}
              </button>
            ))}
            
            {/* Mobile Guests Section */}
            <div className="border-t border-stone-200 pt-2 mt-2">
              <p className="px-4 py-1 text-xs text-[#A8A29E] uppercase tracking-wider">Guests</p>
              {guestMenuItems.map(item => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 w-full text-left px-4 py-2 text-[#57534E] hover:bg-stone-100 rounded-lg"
                >
                  <item.icon size={16} />
                  {item.label}
                </button>
              ))}
            </div>
            
            <div className="border-t border-stone-200 pt-2 mt-2">
              <button
                onClick={onLogout}
                className="block w-full text-left px-4 py-2 text-[#57534E] hover:bg-stone-100 rounded-lg"
              >
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Start Here Guide - only show when data is loaded */}
        {dataLoaded && (
          <StartHereGuide 
            user={user} 
            weddingPlan={weddingPlan}
            tasks={tasks}
            guests={guests}
            budgetItems={budgetItems}
            guestWebsite={guestWebsite}
            chatHistory={chatHistory}
            onOpenVAL={() => {
              setValMode('onboarding');
              setShowVal(true);
            }}
            onOpenGuests={() => setShowGuestOnboarding(true)}
            onOpenBudget={() => setShowBudgetOnboarding(true)}
            onOpenWebsite={() => setShowWebsiteOnboarding(true)}
            onOpenLocation={() => setShowLocationOnboarding(true)}
            onRefresh={loadDashboardData}
            onStepSkipped={(stepId) => {
              // This callback will be passed down from StartHereGuide
              loadDashboardData();
            }}
          />
        )}

        {/* Hero Section */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-[#1C1917] mb-6">
            Welcome, <span className="italic">{user.partner1_name} & {user.partner2_name}</span>
          </h1>
          {weddingPlan?.wedding_date ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* Countdown - Hero Element */}
              {calculateDaysUntil() !== null && (
                <div className="bg-gradient-to-br from-[#881337] to-[#9F1239] rounded-3xl px-8 py-6 text-white shadow-lg">
                  <div className="flex items-baseline gap-2">
                    <span className="text-6xl md:text-7xl font-serif font-light tracking-tight">{calculateDaysUntil()}</span>
                    <span className="text-xl md:text-2xl font-light opacity-90">days</span>
                  </div>
                  <p className="text-rose-200 text-sm mt-1">until your wedding</p>
                </div>
              )}
              
              {/* Wedding Date Info */}
              <div className="bg-white/70 backdrop-blur-xl rounded-2xl px-6 py-4 border border-white/50 shadow-sm">
                <div className="flex items-center gap-3">
                  <Calendar className="text-[#881337]" size={22} />
                  <div>
                    <p className="text-xs text-[#A8A29E] uppercase tracking-wider">Wedding Date</p>
                    <p className="text-lg font-serif text-[#1C1917]">
                      {(() => {
                        // Parse date as local time to avoid timezone shift
                        const [year, month, day] = weddingPlan.wedding_date.split('-').map(Number);
                        const date = new Date(year, month - 1, day);
                        return date.toLocaleDateString('en-US', { 
                          weekday: 'long',
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        });
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/50 max-w-md">
              {showWeddingDateForm ? (
                <form onSubmit={handleSetWeddingDate} className="space-y-4">
                  <p className="text-[#57534E] mb-4">When is your big day?</p>
                  <input
                    data-testid="wedding-date-input"
                    type="date"
                    value={weddingDate}
                    onChange={(e) => setWeddingDate(e.target.value)}
                    className="w-full border-b border-stone-300 focus:border-stone-800 bg-transparent px-0 py-2 outline-none"
                    required
                  />
                  <div className="flex gap-3">
                    <Button 
                      data-testid="save-wedding-date-btn"
                      type="submit" 
                      className="flex-1 rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
                    >
                      Save Date
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setShowWeddingDateForm(false)}
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <Button
                  data-testid="set-wedding-date-btn"
                  onClick={() => setShowWeddingDateForm(true)}
                  className="w-full rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
                >
                  <Calendar className="mr-2" size={18} />
                  Set Your Wedding Date
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Stats Grid - 3 columns without subscription */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <button 
            onClick={() => navigate('/checklist')}
            data-testid="stat-progress" 
            className="bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-lg transition-all duration-300 hover:border-[#881337] text-left cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-[#881337]" size={24} />
              <span className="text-2xl font-serif text-[#881337]">
                {tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0}%
              </span>
            </div>
            <p className="text-sm text-[#57534E]">Overall Progress</p>
            <p className="text-xs text-[#A8A29E] mt-1">Tap to view journey →</p>
          </button>

          <button 
            onClick={() => navigate('/guests')}
            data-testid="stat-guests" 
            className="bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-lg transition-all duration-300 hover:border-[#881337] text-left cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <Users className="text-[#881337]" size={24} />
              <span className="text-2xl font-serif text-[#1C1917]">{guests.length}</span>
            </div>
            <p className="text-sm text-[#57534E]">Total Guests</p>
            <p className="text-xs text-[#A8A29E] mt-1">Tap to manage guests →</p>
          </button>

          <button 
            onClick={() => navigate('/budget')}
            data-testid="stat-budget" 
            className="bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-lg transition-all duration-300 hover:border-[#881337] text-left cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-[#881337]" size={24} />
              <span className="text-2xl font-serif text-[#1C1917]">${totalSpent.toLocaleString()}</span>
            </div>
            <p className="text-sm text-[#57534E]">Budget Spent</p>
            <p className="text-xs text-[#A8A29E] mt-1">Tap to manage budget →</p>
          </button>
        </div>

        {/* What's Next Section */}
        {nextTasks.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif text-[#1C1917]">What's Next</h2>
              <button 
                onClick={() => navigate('/checklist')}
                className="text-sm text-[#881337] hover:text-[#9F1239] flex items-center gap-1"
              >
                View all tasks
                <ChevronRight size={16} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {nextTasks.map((task) => (
                <div 
                  key={task.id}
                  onClick={() => handleTaskAction(task)}
                  className="bg-white rounded-xl p-4 border border-stone-200 hover:shadow-md hover:border-[#881337]/30 transition-all group cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    {/* Complete checkbox */}
                    <button
                      onClick={(e) => handleCompleteTask(task.id, e)}
                      className="w-6 h-6 rounded-full border-2 border-stone-300 hover:border-[#84A98C] hover:bg-[#84A98C]/10 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors group-hover:border-[#881337]"
                      title="Mark as complete"
                    >
                      <CheckCircle size={14} className="text-transparent group-hover:text-stone-300" />
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1C1917] truncate">{task.title}</p>
                      {task.phase && (
                        <p className="text-xs text-[#A8A29E] mt-0.5">{task.phase}</p>
                      )}
                    </div>
                    
                    {/* Action button - always show */}
                    <div
                      className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 transition-colors ${
                        task.action_type === 'search'
                          ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'
                          : task.action_type === 'val'
                          ? 'bg-rose-50 text-rose-600 group-hover:bg-rose-100'
                          : 'bg-[#881337]/10 text-[#881337] group-hover:bg-[#881337]/20'
                      }`}
                    >
                      {task.action_type === 'search' ? (
                        <>
                          <Search size={12} />
                          Find
                        </>
                      ) : task.action_type === 'val' ? (
                        <>
                          <MessageCircle size={12} />
                          Chat
                        </>
                      ) : task.action_type === 'internal' ? (
                        <>
                          Go
                          <ChevronRight size={12} />
                        </>
                      ) : (
                        <>
                          View
                          <ChevronRight size={12} />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions - 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            data-testid="quick-action-val"
            onClick={() => setShowVal(true)}
            className="bg-white rounded-2xl p-8 border border-stone-200 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 text-left group"
          >
            <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <Sparkles className="text-[#881337]" size={24} />
            </div>
            <h3 className="text-xl font-serif mb-2 text-[#1C1917]">Chat with Grace</h3>
            <p className="text-[#57534E] text-sm">Get expert wedding planning advice instantly</p>
          </button>

          <button
            data-testid="quick-action-checklist"
            onClick={() => navigate('/checklist')}
            className="bg-white rounded-2xl p-8 border border-stone-200 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 text-left group"
          >
            <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <ListChecks className="text-[#881337]" size={24} />
            </div>
            <h3 className="text-xl font-serif mb-2 text-[#1C1917]">View Checklist</h3>
            <p className="text-[#57534E] text-sm">See your personalized planning tasks</p>
          </button>
        </div>
      </div>

      {/* Grace Assistant */}
      {showVal && <VALAssistant user={user} onClose={async () => {
        // Always mark Grace task complete if user has had a conversation
        // The chatHistory will be refreshed after loadDashboardData
        await markTasksComplete(['Chat with Grace', 'Chat with Grace about your vision', 'meet grace', 'ai assistant']);
        setShowVal(false);
        setValMode('general');
        loadDashboardData();
      }} onMessageSent={async () => {
        // Mark Grace task complete immediately when user sends a message
        await markTasksComplete(['Chat with Grace', 'Chat with Grace about your vision', 'meet grace', 'ai assistant']);
        loadDashboardData();
      }} mode={valMode} />}

      {/* Feedback Survey */}
      <FeedbackSurvey
        isOpen={showFeedbackSurvey}
        onClose={() => setShowFeedbackSurvey(false)}
        user={user}
      />

      {/* Quick Feedback Prompt (Periodic Check-ins) */}
      <QuickFeedbackPrompt
        isOpen={showQuickPrompt}
        onClose={handleQuickPromptClose}
        user={user}
        dayNumber={promptDayNumber}
      />

      {/* Onboarding Modals */}
      <GuestListOnboarding
        isOpen={showGuestOnboarding}
        onClose={(completed, skipped) => {
          setShowGuestOnboarding(false);
          if (skipped) markStepAsSkipped('add_guests');
          if (completed) markTasksComplete(['Add your first guests', 'guest list', 'add guest']);
          if (completed || skipped) loadDashboardData();
        }}
        userId={user.id}
        onGuestAdded={loadDashboardData}
      />

      <BudgetOnboarding
        isOpen={showBudgetOnboarding}
        onClose={(completed, skipped) => {
          setShowBudgetOnboarding(false);
          if (skipped) markStepAsSkipped('set_budget');
          if (completed) markTasksComplete(['Set your budget', 'budget']);
          if (completed || skipped) loadDashboardData();
        }}
        userId={user.id}
      />

      <WebsiteOnboarding
        isOpen={showWebsiteOnboarding}
        onClose={(completed, skipped) => {
          setShowWebsiteOnboarding(false);
          if (skipped) markStepAsSkipped('create_website');
          if (completed) markTasksComplete(['Create your guest website', 'guest website', 'website']);
          if (completed || skipped) loadDashboardData();
        }}
        userId={user.id}
      />

      <LocationOnboarding
        isOpen={showLocationOnboarding}
        onClose={(completed, skipped) => {
          setShowLocationOnboarding(false);
          if (skipped) markStepAsSkipped('set_location');
          if (completed) markTasksComplete(['Set wedding location', 'wedding location']);
          if (completed || skipped) loadDashboardData();
        }}
        userId={user.id}
        onLocationSet={loadDashboardData}
      />

      {/* Floating Feedback Button (Test Users Only) */}
      {user.subscription_status === 'test_trial' && !showFeedbackSurvey && (
        <button
          onClick={() => setShowFeedbackSurvey(true)}
          className="fixed bottom-28 right-8 px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 flex items-center gap-2 z-40"
        >
          <MessageSquare size={20} />
          <span className="font-medium">Share Feedback</span>
        </button>
      )}

      {/* Floating Grace Button */}
      {!showVal && (
        <button
          data-testid="floating-val-btn"
          onClick={() => setShowVal(true)}
          className="fixed bottom-8 right-8 w-16 h-16 rounded-full bg-gradient-to-br from-[#881337] to-[#9F1239] text-white shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 flex items-center justify-center z-40"
        >
          <Sparkles size={24} />
        </button>
      )}
    </div>
  );
};

export default Dashboard;